import { getFormProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import * as E from '@react-email/components';
import { type ActionFunctionArgs, type LoaderFunctionArgs, redirect } from '@remix-run/node';
import { Form, useActionData, useNavigation } from '@remix-run/react';
import { Button } from 'flowbite-react';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { defaultLang } from '#buyer-portal/i18n/config.ts';
import i18nServer, { getLocaleForRequest } from '#buyer-portal/i18n/i18n.server';
import { getUserId } from '#buyer-portal/session.server';
import { FormInputField } from '#common/components/catalyst-ui-extensions/FormInputField.js';
import { FormTextareaField } from '#common/components/catalyst-ui-extensions/FormTextareaField.js';
import { Spinner } from '#common/components/Spinner.js';
import { type LayoutHandle } from '#common/hooks/useLayoutTitleKey.ts';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { sendEmail } from '#common/services/email.server.js';
import { checkHoneypot } from '#common/utils/honeypot.server.js';
import { translatePath } from '#common/utils/i18n.tsx';
import { PrefixWithEnv } from '#common/utils/misc.tsx';
import { tenants } from '../../../../../other/tenants';

export const registerSchema = z.object({
    name: z.string().trim().min(1, 'Name is required'),
    email: z.string().trim().email('A valid email address is required'),
    company: z.string().trim().optional(),
    message: z.string().trim().min(1, 'Message is required').max(1000, 'Message is too long')
});

export const loader = async ({ request }: LoaderFunctionArgs) => {
    const userId = await getUserId(request);
    if (userId) {
        throw redirect('/account');
    }

    const url = new URL(request.url);
    const locale = await getLocaleForRequest(null, request);
    const t = await i18nServer.getFixedT(locale, 'register');
    const voiceArtistsPath = t('voiceArtistsPath');

    return typedjson({
        voiceArtistsUrl: new URL(
            translatePath(voiceArtistsPath, locale, { defaultLang }),
            `${url.protocol}//${tenants.website.hosts[0]}`
        ).toString()
    });
};

function RegisterRequestEmail(data: z.infer<typeof registerSchema>) {
    return (
        <E.Tailwind>
            <E.Html lang='en' dir='ltr'>
                <E.Preview>{data.name} sent a buyer portal registration request.</E.Preview>
                <E.Body>
                    <E.Container>
                        <E.Heading>New buyer portal registration request</E.Heading>
                        <E.Row>
                            <E.Column>
                                <E.Text>
                                    <strong>Name:</strong>
                                </E.Text>
                            </E.Column>
                            <E.Column>
                                <E.Text>{data.name}</E.Text>
                            </E.Column>
                        </E.Row>
                        <E.Row>
                            <E.Column>
                                <E.Text>
                                    <strong>Email:</strong>
                                </E.Text>
                            </E.Column>
                            <E.Column>
                                <E.Text>{data.email}</E.Text>
                            </E.Column>
                        </E.Row>
                        <E.Row>
                            <E.Column>
                                <E.Text>
                                    <strong>Company:</strong>
                                </E.Text>
                            </E.Column>
                            <E.Column>
                                <E.Text>{data.company || 'Not provided'}</E.Text>
                            </E.Column>
                        </E.Row>
                        <E.Row>
                            <E.Column>
                                <E.Text>
                                    <strong>Message:</strong>
                                </E.Text>
                            </E.Column>
                        </E.Row>
                        <E.Row>
                            <E.Column>
                                <E.Text>{data.message}</E.Text>
                            </E.Column>
                        </E.Row>
                    </E.Container>
                </E.Body>
            </E.Html>
        </E.Tailwind>
    );
}

export const action = async ({ request }: ActionFunctionArgs) => {
    const formData = await request.formData();
    await checkHoneypot(formData);

    const submission = parseWithZod(formData, { schema: registerSchema });
    if (submission.status !== 'success') {
        return typedjson(
            { result: submission.reply(), submitted: false },
            { status: submission.status === 'error' ? 400 : 200 }
        );
    }

    const sendResult = await sendEmail({
        to: process.env.CONTACT_FORM_EMAIL!,
        subject: PrefixWithEnv('CrispyVoice: New buyer portal registration request'),
        react: <RegisterRequestEmail {...submission.value} />
    });

    if (sendResult.status !== 'success') {
        return typedjson(
            {
                result: submission.reply({
                    formErrors: [ 'Could not send your request right now. Please try again in a moment.' ]
                }),
                submitted: false
            },
            { status: 500 }
        );
    }

    return typedjson({ result: submission.reply({ resetForm: true }), submitted: true });
};

export default function RegisterPage() {
    const { voiceArtistsUrl } = useTypedLoaderData<typeof loader>();
    const actionData = useActionData<typeof action>();
    const navigation = useNavigation();
    const isSubmitting = navigation.state === 'submitting';
    const { t, rawT } = useEditableTranslation('register');

    const [ form, fields ] = useForm({
        lastResult: actionData?.result,
        constraint: getZodConstraint(registerSchema),
        shouldValidate: 'onBlur',
        shouldRevalidate: 'onInput',
        onValidate({ formData }) {
            return parseWithZod(formData, { schema: registerSchema });
        }
    });

    if (actionData?.submitted) {
        return (
            <>
                <div
                    role='status'
                    className='mt-4 rounded-lg border border-green-300 bg-green-50 px-6 py-5 text-center dark:border-green-700 dark:bg-green-900/30'
                >
                    <p className='text-lg font-semibold text-green-800 dark:text-green-200'>{t('success')}</p>
                </div>

                <p className='text-sm mt-5 text-center'>
                    <a
                        href={voiceArtistsUrl}
                        className='underline cursor-pointer font-medium dark:text-crispyblue-500 text-crispyblue-700'
                    >
                        {t('voiceArtistsLink')}
                    </a>
                </p>
            </>
        );
    }

    return (
        <>
            <div className='mb-6 text-center'>
                <p className='text-sm text-gray-600 dark:text-gray-300'>{t('subHeading')}</p>
            </div>

            <Form method='post' className='space-y-4' {...getFormProps(form)}>
                <HoneypotInputs />

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    <FormInputField
                        field={fields.name}
                        label={t('name')}
                        placeholder={rawT('namePlaceholder')}
                        autoComplete='name'
                    />

                    <FormInputField
                        field={fields.email}
                        label={t('email')}
                        type='email'
                        placeholder={rawT('emailPlaceholder')}
                        autoComplete='email'
                    />
                </div>

                <FormInputField
                    field={fields.company}
                    label={t('company')}
                    placeholder={rawT('companyPlaceholder')}
                    autoComplete='organization'
                />

                <FormTextareaField
                    field={fields.message}
                    label={t('message')}
                    placeholder={rawT('messagePlaceholder')}
                    rows={4}
                />

                <Button type='submit' disabled={isSubmitting} className='w-full h-10 cursor-pointer'>
                    {isSubmitting ? <Spinner size='sm' /> : t('submit')}
                </Button>
            </Form>

            <p className='text-sm mt-5 text-center text-gray-600 dark:text-gray-300'>{t('hint')}</p>

            <p className='text-sm mt-3 text-center'>
                <a
                    href={voiceArtistsUrl}
                    className='underline cursor-pointer font-medium dark:text-crispyblue-500 text-crispyblue-700'
                >
                    {t('voiceArtistsLink')}
                </a>
            </p>
        </>
    );
}

export const handle: LayoutHandle & { i18n: string[] } = { layoutTitleKey: 'register', i18n: [ 'register' ] };
